/**
 * MONSTER TOWN — THE KID'S OWN TWO GENTLE NEEDS. ROUTINE CREW (src/routine/**).
 *
 * "A DAY IN TOWN" §2: *The player's monster has gentle needs: a little sleepy at
 * night, a little hungry in the morning. Both are invitations, never punishments
 * — nothing bad ever happens if ignored, no meters drain to zero, no sad faces.
 * Eating breakfast and sleeping simply feel good.*
 *
 * ===========================================================================
 * WHAT AN INVITATION IS ALLOWED TO BE, IN THIS FILE
 * ===========================================================================
 *  - A soft thought bubble that drifts up over the monster for four seconds
 *    with a PICTURE of the thing in it — a bed, a bowl of breakfast (LAW #4).
 *  - Shown when a need first turns warm, and at most once every three minutes
 *    after that while it stays warm. Never a badge that sits there. Never a
 *    counter. Never a red anything.
 *  - It is NOT tappable, on purpose. A thing floating over a walking monster
 *    cannot be a reliable 88 px target, and a target a fat finger misses is a
 *    worse experience than no target — the bed is right there in the bedroom
 *    and the fridge is right there in the kitchen.
 *
 * WHAT IT CANNOT BE, STRUCTURALLY
 *  - There is no meter, because `warmth` is recomputed from the clock every
 *    time it is asked (see the header of `src/core/schedule.ts`) and nothing in
 *    this file stores one.
 *  - There is no penalty, because the only economy call here is `earnExact`,
 *    which cannot take a negative number, and it is only reached on a HAPPY
 *    event.
 *  - Nothing is gated: no code path in this file blocks a tap, a door, a build
 *    or a venue. Ignore both needs for ever and the game is identical.
 *
 * THE BED IS ALSO THE CLOCK — AND THAT WIRE IS NOT OURS. `src/time/sleep.ts`
 * moves the clock and runs the sunrise show; this file only notices that the kid
 * went to bed, which is what the bedtime invitation was inviting. Both files
 * once did the jump and one tap on the duvet cost a kid two days — see the note
 * at the `category === 'bed'` branch below.
 */

import * as THREE from 'three'
import { bus } from '../core/events'
import { engine } from '../core/engine'
import { wallet } from '../core/economy'
import { hex } from '../core/palette'
import { getPlaceable } from '../core/placeable'
import { needs, type NeedId } from '../core/schedule'
import { world } from '../core/scene'
import { clock } from '../core/time'
import type { PlaceableId } from '../core/types'
import { app, type GameContext } from '../app/context'
import { ball, box, group, inkAll } from '../render/kit'
import { playerRig } from '../monster/player'

/** How long a bubble stays up. Long enough to notice, short enough to ignore. */
const BUBBLE_SECONDS = 4
/** The gentlest possible re-offer: three real minutes, and only while warm. */
const REOFFER_SECONDS = 180
/**
 * WHERE THE CLOUD SITS, RELATIVE TO THE ANCHOR ABOVE THE MONSTER'S HEAD.
 *
 * The bubble is billboarded, so these are screen right and screen up in every
 * one of the camera's four rotations. Up AND across, because straight up is not
 * enough: a fixed iso camera foreshortens vertical world offsets to about 0.82,
 * so a bubble that measures clear of the head in world units lands on its face
 * on the glass — which is exactly what `ui-08c/08d` show, with the one monster
 * that is "you" having no face at all.
 */
const OFFSET_X = 0.85
const OFFSET_Y = 0.95

// ---------------------------------------------------------------------------
// the thought bubble
// ---------------------------------------------------------------------------

interface Bubble {
  root: THREE.Group
  bed: THREE.Group
  bowl: THREE.Group
}

interface Tinted {
  mat: THREE.Material & { color: THREE.Color }
  base: THREE.Color
  /** How far this surface follows the night. See CLOUD/SYMBOL_NIGHT_PULL. */
  pull: number
  /** How much of its own daylight colour it keeps lit. See *_NIGHT_LIFT. */
  lift: number
  /** The clone's own emissive, when it has one. Null for an unlit material. */
  glow: THREE.Color | null
}

let bubble: Bubble | null = null
let showing: NeedId | null = null
let bubbleT = 0
let attachedTo: THREE.Object3D | null = null
/** This bubble's OWN materials, so night can be painted on without side effects. */
let tints: Tinted[] = []

/** Deep blue-plum — the same mix `src/time/tint.ts` washes the town with. */
const NIGHT = new THREE.Color(hex('shadow')).lerp(new THREE.Color(hex('ink')), 0.45)
/**
 * ===========================================================================
 * A THOUGHT BUBBLE IS UI-IN-THE-WORLD, AND IT KEEPS ITS OWN VALUE AFTER DARK
 * ===========================================================================
 * ROUND 2 DEFECT: *"the sleepy bubble reads as a grey boulder at night —
 * at plaza distance it is a rock, or a hole in the ground sitting in the
 * fountain basin, not somebody thinking about bed."*
 *
 * MEASURED, off the committed frames that were complained about
 * (`shots/critic-vr2/street-night.png`, sampled before a line was changed):
 * the cloud's own pixels came back at luminance **76-87**, against fountain
 * stone at **86.6** and plaza path at **78.3 IN THE SAME FRAME**. The puff was
 * not merely dark — it was *the same value as the rock it was floating in front
 * of*. There is no reading of that picture in which it is a cloud.
 *
 * THREE DARKENINGS, AND ROUND 1 ONLY COUNTED ONE. A white puff at night is
 * ground down by, in order:
 *   1. this file's own night pull toward `NIGHT`,
 *   2. the LIGHTING RIG — it is a `MeshToonMaterial`, so the dimmed night sun
 *      and cooled hemisphere scale its albedo like every other surface,
 *   3. the time crew's full-screen MULTIPLY grade (`src/time/tint.ts`).
 * Round 1 saw the puff out-glowing the lamp posts and answered by winding (1)
 * up to 0.62 — which, stacked on (2) and (3), is what put it in the ground.
 *
 * SO THE PULL IS NO LONGER THE VALUE KNOB. It only decides how far the bubble
 * COOLS toward the night's colour, which is what makes it belong to the scene.
 * How much of itself it keeps LIT is now its own explicit number:
 *
 *   *_NIGHT_LIFT — an emissive floor, `base * lift * darkness`, i.e. each
 *   surface keeps a share of ITS OWN DAYLIGHT COLOUR alight after dark. Of its
 *   own colour, not of white, so the duvet glows teal and the bed's dark frame
 *   stays dark: the silhouette inside the puff survives by construction rather
 *   than by a second tuning pass.
 *
 * It is exactly zero at `darkness === 0`, so the DAYTIME bubble is
 * bit-identical to the shipped one — `fix-routine-r2` gates that as well as the
 * night contrast, so neither half of this can be fixed by breaking the other.
 * Emissive is a uniform the toon shader always carries, so this costs no extra
 * material, no extra draw call and no shader recompile.
 *
 * AND THE CEILING STILL STANDS: round 1 was right that a puff must not out-glow
 * the town. Measured after this change it sits well under the lamp heads and
 * under the HUD, and `fix-routine-r2` keeps that assertion alongside the new
 * one.
 */
const CLOUD_NIGHT_PULL = 0.3
const SYMBOL_NIGHT_PULL = 0.2
const CLOUD_NIGHT_LIFT = 0.42
/**
 * The picture inside the puff keeps less of itself alight than the puff does —
 * it is drawn ON the cloud, so it needs to stay the DARKER of the two or the
 * bed dissolves into its own background. Enough to hold the duvet's teal and
 * the sleeping monster's coral, not enough to lift the bed frame off the white.
 */
const SYMBOL_NIGHT_LIFT = 0.14

/**
 * THE BUBBLE OWNS ITS COLOURS.
 *
 * `src/render/kit.ts` caches and SHARES materials by colour — that is the whole
 * point of it, and it means `bubble.mat.color.set(...)` would repaint every
 * white thing in the game. So every material this bubble uses is cloned once, at
 * build time, and the clones are what get tinted. Called BEFORE `inkAll`, so the
 * outline shells keep the one shared ink material and the night pass never
 * touches it.
 */
function ownMaterials(root: THREE.Object3D, pull: number, lift: number): void {
  const seen = new Map<THREE.Material, THREE.Material>()
  root.traverse((o) => {
    const m = o as THREE.Mesh
    if (m.isMesh !== true) return
    const mat = m.material
    if (Array.isArray(mat) || mat === undefined) return
    let mine = seen.get(mat)
    if (mine === undefined) {
      mine = mat.clone()
      seen.set(mat, mine)
      const withColor = mine as THREE.Material & {
        color?: THREE.Color
        emissive?: THREE.Color
      }
      const c = withColor.color
      if (c !== undefined) {
        tints.push({
          mat: mine as THREE.Material & { color: THREE.Color },
          base: c.clone(),
          pull,
          lift,
          glow: withColor.emissive ?? null,
        })
      }
    }
    m.material = mine
  })
}

/** Own the colours, then ink the silhouette. One helper, four callers. */
function skin(g: THREE.Group, pull: number, lift: number): THREE.Group {
  ownMaterials(g, pull, lift)
  return inkAll(g)
}

/**
 * Built once, hidden the rest of the time. Two symbols, one puff of cloud.
 *
 * SIZED AND PLACED FROM CAPTURES, NOT FROM A GUESS.
 *
 * v1 was a 0.9-unit puff and read as a small white blob. v2 made it big enough
 * to read and introduced two worse problems, both visible in
 * `shots/critic-vr1/zoom-night-lamp-pool.png` and both fixed here:
 *
 *  - IT COVERED THE MONSTER. Centred over the head, with a "tail" of two balls
 *    stacked straight down INSIDE the cloud where nothing could see them. So it
 *    had no tail and it sat on the one face in the game that is the kid. The
 *    cloud now sits up and to one side and the tail is three shrinking puffs
 *    strung BETWEEN the head and the cloud, which is what makes a thought
 *    bubble point at somebody.
 *  - THE BED DID NOT READ AS A BED, and could not have. It was modelled as a
 *    mattress in PLAN — 0.8 wide, 0.4 deep, 0.16 tall — and then billboarded, so
 *    the camera only ever saw its 0.8 x 0.16 edge: a brown box with a teal
 *    stripe, exactly as reported. Every piece is now drawn in the billboard
 *    plane as a SIDE ELEVATION: headboard, footboard, frame, legs, mattress,
 *    turned-down teal blanket, pillow — and a monster asleep on it with its eyes
 *    shut, because "somebody is asleep" is a picture a three-year-old reads in
 *    one glance and "a bed" is furniture.
 */
function buildBubble(): Bubble {
  const root = group('needBubble')
  // The three shrinking puffs that make it a THOUGHT, pointing at the monster.
  const tail = group('needTail')
  tail.add(ball(0.09, { color: 'uiPanel', x: 0.1, y: -0.05 }))
  tail.add(ball(0.13, { color: 'uiPanel', x: 0.32, y: 0.2 }))
  tail.add(ball(0.18, { color: 'uiPanel', x: 0.58, y: 0.5 }))
  root.add(skin(tail, CLOUD_NIGHT_PULL, CLOUD_NIGHT_LIFT))

  // Everything else hangs off one group, offset clear of the monster's head.
  const cloud = group('cloud')
  cloud.add(ball(0.72, { color: 'uiPanel', y: 0.18 }))
  cloud.add(ball(0.48, { color: 'uiPanel', x: -0.52, y: 0.02 }))
  cloud.add(ball(0.48, { color: 'uiPanel', x: 0.52, y: 0.02 }))
  cloud.add(ball(0.4, { color: 'uiPanel', x: -0.3, y: 0.5 }))
  cloud.add(ball(0.4, { color: 'uiPanel', x: 0.34, y: 0.46 }))
  // The bottom of the puff. Without it the bed's legs hang out of the cloud.
  cloud.add(ball(0.44, { color: 'uiPanel', x: 0.02, y: -0.2 }))
  cloud.position.set(OFFSET_X, OFFSET_Y, 0)
  root.add(skin(cloud, CLOUD_NIGHT_PULL, CLOUD_NIGHT_LIFT))

  /*
   * A BED, IN ELEVATION, WITH SOMEBODY ASLEEP IN IT.
   *
   * EVERY VALUE IS PICKED AGAINST A WHITE CLOUD, which is the mistake the first
   * two versions both made. A cream mattress and a white pillow on a white puff
   * have no silhouette — the same trap the world crew hit with a white fridge on
   * a mint wall, written into this file's own comments and then walked into
   * anyway. So the big shapes are the DARK ones (a chunky wooden frame) and the
   * SATURATED one (a fat teal duvet), and the pale pieces are small and outlined.
   *
   * "A bed" is furniture. "Somebody asleep" is a story, and a three-year-old
   * reads it in one glance — so there is a monster under the duvet with its head
   * on the pillow and its eyes shut.
   */
  const bed = group('needBed')
  bed.add(box(0.18, 0.72, 0.34, { color: 'woodDark', x: -0.62, y: 0.02 }))
  bed.add(box(0.16, 0.4, 0.34, { color: 'woodDark', x: 0.66, y: -0.14 }))
  bed.add(box(1.44, 0.16, 0.34, { color: 'woodDark', y: -0.3 }))
  bed.add(box(0.12, 0.2, 0.3, { color: 'woodDark', x: -0.55, y: -0.48 }))
  bed.add(box(0.12, 0.2, 0.3, { color: 'woodDark', x: 0.58, y: -0.48 }))
  bed.add(box(1.2, 0.2, 0.36, { color: 'uiPanelAlt', y: -0.12 }))
  bed.add(box(0.86, 0.3, 0.4, { color: 'uiAccent', x: 0.3, y: 0.05 }))
  bed.add(box(0.1, 0.32, 0.42, { color: 'uiAccentDark', x: -0.16, y: 0.06 }))
  bed.add(box(0.36, 0.22, 0.34, { color: 'uiPanelAlt', x: -0.36, y: 0.06 }))
  bed.add(ball(0.21, { color: 'monsterCoral', x: -0.3, y: 0.24 }))
  // Shut eyes, sat proud of the head so the sphere cannot swallow them.
  bed.add(box(0.09, 0.04, 0.03, { color: 'ink', x: -0.38, y: 0.26, z: 0.23 }))
  bed.add(box(0.09, 0.04, 0.03, { color: 'ink', x: -0.2, y: 0.26, z: 0.23 }))
  bed.scale.setScalar(0.78)
  bed.position.set(OFFSET_X, OFFSET_Y + 0.06, 0.66)
  root.add(skin(bed, SYMBOL_NIGHT_PULL, SYMBOL_NIGHT_LIFT))

  // A BOWL OF BREAKFAST: a dish with two round things in it. This one already
  // read correctly in the captures; it has only moved with the cloud.
  const bowl = group('needBowl')
  const dish = ball(0.4, { color: 'uiPanelAlt', y: 0.08 })
  dish.scale.set(1, 0.5, 1)
  bowl.add(dish)
  bowl.add(ball(0.17, { color: 'flowerRed', x: -0.14, y: 0.22 }))
  bowl.add(ball(0.15, { color: 'monsterLime', x: 0.16, y: 0.2 }))
  bowl.position.set(OFFSET_X, OFFSET_Y, 0.66)
  root.add(skin(bowl, SYMBOL_NIGHT_PULL, SYMBOL_NIGHT_LIFT))

  root.visible = false
  bed.visible = false
  bowl.visible = false
  return { root, bed, bowl }
}

/**
 * NIGHT HAS TO REACH THE BUBBLE — AND THEN STOP.
 *
 * The cloud is `uiPanel`, pure white, and the town after dark is a wash of deep
 * blue-plum over a palette that has already been dimmed. Left alone it out-glows
 * every lamp post in the frame; washed like a wall it becomes one (see the long
 * note at CLOUD_NIGHT_PULL — that is the round-2 defect). So night does two
 * things here and they pull opposite ways on purpose:
 *
 *   COLOUR   cooled toward `NIGHT`, so the puff belongs to the picture.
 *   EMISSIVE lifted back off its own base colour, so the puff keeps enough of
 *            its own value to stay a puff, and the picture inside it keeps its
 *            hue instead of going to slate.
 *
 * Both terms are exactly zero at darkness 0, so nothing changes by day.
 * `clock.darknessAt` is pure and costs nothing, and the materials are only
 * written when the answer has actually moved.
 */
let tintAt = -1
function tintForNight(): void {
  const darkness = clock.darknessAt(clock.t)
  if (Math.abs(darkness - tintAt) < 0.02) return
  tintAt = darkness
  for (const t of tints) {
    t.mat.color.copy(t.base).lerp(NIGHT, t.pull * darkness)
    // `base`, not white: a surface keeps a share of ITS OWN daylight colour, so
    // the duvet glows teal and the bed's dark frame stays dark against the puff.
    t.glow?.copy(t.base).multiplyScalar(t.lift * darkness)
  }
}

function ensureAttached(): Bubble | null {
  if (bubble === null) bubble = buildBubble()
  const id = world.activeSceneId
  const node = id === null ? undefined : world.get(id)
  if (node === undefined) return bubble
  if (attachedTo !== node.actors) {
    bubble.root.parent?.remove(bubble.root)
    node.actors.add(bubble.root)
    attachedTo = node.actors
  }
  return bubble
}

function showBubble(id: NeedId): void {
  const b = ensureAttached()
  if (b === null) return
  showing = id
  bubbleT = 0
  b.bed.visible = id === 'sleepy'
  b.bowl.visible = id === 'hungry'
  b.root.visible = true
  b.root.scale.setScalar(0.01)
}

function hideBubble(): void {
  showing = null
  if (bubble !== null) bubble.root.visible = false
}

/** Follow the monster and face the lens. Only runs while a bubble is up. */
function renderBubble(ctx: GameContext, frameMs: number): void {
  if (showing === null || bubble === null) return
  bubbleT += Math.min(0.1, frameMs / 1000)
  if (bubbleT >= BUBBLE_SECONDS) {
    hideBubble()
    return
  }
  const rig = playerRig()
  const height = rig === null ? 1.7 : rig.height
  const p = app.playerPos
  const rise = Math.min(1, bubbleT / 0.35)
  const fade = Math.min(1, (BUBBLE_SECONDS - bubbleT) / 0.5)
  const s = Math.min(rise, fade)
  tintForNight()
  // The ANCHOR is just above the head; the cloud's own offset carries it clear
  // (see OFFSET_X/OFFSET_Y), and scaling the root grows the whole thing out of
  // the monster rather than dropping it on top of them.
  bubble.root.position.set(p.x, height + 0.28 + Math.sin(bubbleT * 1.6) * 0.07, p.z)
  bubble.root.scale.setScalar(0.25 + s * 0.95)
  // A fixed iso camera only ever sits at four yaws, but copying the quaternion
  // is one operation and cannot be wrong at any of them.
  bubble.root.quaternion.copy(ctx.camera.camera.quaternion)
}

// ---------------------------------------------------------------------------
// accepting an invitation
// ---------------------------------------------------------------------------

const lastOffer = new Map<NeedId, number>()

function offer(id: NeedId): void {
  const now = Date.now() / 1000
  const last = lastOffer.get(id) ?? -Infinity
  if (now - last < REOFFER_SECONDS) return
  lastOffer.set(id, now)
  showBubble(id)
  bus.emit('audio:cue', { cue: 'pop_soft', volume: 0.35 })
}

/**
 * The kid did the thing. `satisfy()` can only ever hand back a non-negative
 * bonus, once per in-world day — so this is a nice moment, never a faucet and
 * never a bill.
 */
function accept(id: NeedId, source: string): void {
  needs.satisfy(id, source)
}

export function installInvitations(ctx: GameContext): void {
  // A need turned warm. Not a warning — the world is just pleased to see you.
  // Taken off the BUS rather than off `needs.onInvite`, deliberately: the event
  // is the published contract, and `wiring-check` can see a bus subscriber.
  bus.on('need:invited', (n) => offer(n.id))

  /**
   * EVERY TAP IN THE GAME THAT COUNTS AS EATING OR SLEEPING.
   *
   * Driven off the placeable CATEGORY, not off a list of ids, so a new bed or a
   * new cooker from any crew is picked up as a data drop with nothing to wire.
   */
  bus.on('play:interacted', (e) => {
    const def = getPlaceable(e.defId as PlaceableId)
    if (def === undefined) return
    if (def.category === 'bed') {
      /*
       * THE BED IS ALSO THE CLOCK — and moving it is NOT this crew's job.
       *
       * `src/time/sleep.ts` owns that wire, together with the eyelids-and-
       * sunrise show that makes the jump watchable. This file used to call
       * `clock.sleepToMorning()` here as well, and the two together cost a kid
       * TWO days for one tap on the duvet: `routine-check` caught it as
       * "day 1@22:00 -> day 3@07:00". One bed, one morning, one owner.
       *
       * What is ours is what the tap MEANS: the kid went to bed, so the gentle
       * bedtime invitation has been accepted.
       */
      accept('sleepy', 'bed')
      return
    }
    if (def.category === 'kitchen') accept('hungry', e.defId)
  })

  /**
   * THE HAPPY PART. A sparkle where the monster is standing, a picture of what
   * they just did, and a few coins. `bonusCoins` is 0 when the invitation was
   * not warm or has already been accepted today, and 0 coins means no fuss at
   * all — accepting a need out of hours is fine and simply quiet.
   */
  bus.on('need:satisfied', (e) => {
    if (e.bonusCoins <= 0) return
    const at = { x: app.playerPos.x, y: 0.8, z: app.playerPos.z }
    bus.emit('fx:play', { effect: 'sparkle', at, count: 12 })
    bus.emit('ui:toast', { icon: e.id === 'sleepy' ? 'bed' : 'dinner', ms: 1800 })
    bus.emit('audio:cue', { cue: 'chime_happy', volume: 0.6 })
    wallet.earnExact(e.bonusCoins, 'day.wellRested', at)
    hideBubble()
  })

  /**
   * Waking up somewhere new deserves a beat of its own — but a SMALL one. The
   * time crew's sunrise show (`src/time/sleep.ts`) is the moment; this is a
   * handful of stars over the monster who just woke up, and no second badge.
   */
  bus.on('time:slept', () => {
    hideBubble()
    bus.emit('fx:play', {
      effect: 'stars',
      at: { x: app.playerPos.x, y: 1.2, z: app.playerPos.z },
      count: 10,
    })
  })

  bus.on('scene:left', () => hideBubble())
  bus.on('save:loaded', () => {
    hideBubble()
    lastOffer.clear()
  })

  // Render only: the bubble follows the monster while it is up, and the whole
  // system costs one boolean test a frame the rest of the time.
  engine.addSystem({
    id: 'routine.invitations',
    order: 43,
    render: (_alpha, frameMs) => renderBubble(ctx, frameMs),
  })
}

// ---------------------------------------------------------------------------
// debug / QA surface
// ---------------------------------------------------------------------------

/**
 * Is the gentle bubble on screen, which picture is in it, and WHERE.
 *
 * `at` is the world centre of the cloud and `radius` its half-width at the
 * current scale, both of which the gate needs to prove the thing is not sitting
 * on the monster's face — an assertion nobody could write while the only
 * published facts were "visible: true".
 */
export function bubbleState(): {
  showing: string | null
  visible: boolean
  t: number
  at: { x: number; y: number; z: number } | null
  radius: number
} {
  const up = bubble !== null && bubble.root.visible
  let at: { x: number; y: number; z: number } | null = null
  let radius = 0
  if (bubble !== null && up) {
    const cloud = bubble.root.getObjectByName('cloud')
    if (cloud !== undefined) {
      const w = new THREE.Vector3()
      cloud.getWorldPosition(w)
      at = { x: w.x, y: w.y, z: w.z }
      radius = 0.72 * bubble.root.scale.x
    }
  }
  return { showing, visible: up, t: bubbleT, at, radius }
}

/** Force an invitation now, ignoring the re-offer cooldown (QA only). */
export function offerNow(id: NeedId): void {
  lastOffer.delete(id)
  offer(id)
}
